/**
 * Advanced Question Bank: Domain 6 — Attention & Processing Speed (الانتباه وسرعة المعالجة)
 * 15 Psychometrically Rigorous Generative Template Families (Processing Speed Gs / Perceptual Acuity)
 */

import { renderOptionSvg, DrawShapes, wrapSvg } from './svg-builder.js';

export const ADVANCED_SPEED_TEMPLATES = [
  // 1. Eriksen Flanker Inhibitory Task
  {
    id: 'speed_flanker_inhibitory',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      // Incongruent flankers: e.g. << > << (Center arrow is target, facing RIGHT)
      const targetRight = prng.pick([true, false]);
      const flankersRight = !targetRight; // strictly incongruent for high psychometric discrimination

      const targetChar = targetRight ? '▶' : '◀';
      const flankerChar = flankersRight ? '▶' : '◀';
      const flankerDisplay = `${flankerChar} ${flankerChar} ${targetChar} ${flankerChar} ${flankerChar}`;

      const options = [
        { id: 'A', text_ar: 'يشير إلى اليمين (Right ▶)', text_en: 'Pointing Right (▶)', value: 'right' },
        { id: 'B', text_ar: 'يشير إلى اليسار (Left ◀)', text_en: 'Pointing Left (◀)', value: 'left' },
        { id: 'C', text_ar: 'يشير للأعلى (Up ▲)', text_en: 'Pointing Up (▲)', value: 'up' },
        { id: 'D', text_ar: 'الأسهم متعارضة ولا يوجد سهم مركزي', text_en: 'Conflicting without center', value: 'none' }
      ];

      const correctIndex = targetRight ? 0 : 1;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار كفاءة التثبيط الإدراكي وسرعة الاستجابة (Eriksen Flanker Task):\nركز نظرك بأقصى سرعة على **السهم الأوسط فقط** متجاهلاً الأسهم الجانبية المحيطة به:\n\n[   ${flankerDisplay}   ]\n\nإلى أي اتجاه يشير السهم الأوسط المستهدف؟`,
        questionText_en: `In the Eriksen Flanker Task, focus exclusively on the **CENTER ARROW**, actively suppressing flanking arrows:\n\n[   ${flankerDisplay}   ]\n\nWhich direction does the central target arrow point?`,
        options,
        correctIndex,
        explanation_ar: `السهم الأوسط يقع في المركز الثالث بدقة [ ${targetChar} ]، وهو يشير بوضوح إلى ${targetRight ? 'اليمين' : 'اليسار'}، متجاوزًا التشتيت البصري المعاكس للأسهم المحيطة (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The central target arrow is ${targetChar}, facing strictly ${targetRight ? 'Right' : 'Left'}, confirming option (${options[correctIndex].id}).`
      };
    }
  },

  // 2. Classical Stroop Interference (Color-Word Concordance)
  {
    id: 'speed_stroop_color_word',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      // Word is written in a conflicting font color: e.g. the word "أحمر" written in Blue font
      const colors = [
        { name_ar: 'أحمر', name_en: 'Red', hex: '#ef4444' },
        { name_ar: 'أزرق', name_en: 'Blue', hex: '#3b82f6' },
        { name_ar: 'أخضر', name_en: 'Green', hex: '#10b981' },
        { name_ar: 'أصفر', name_en: 'Yellow', hex: '#f59e0b' }
      ];

      const [wordObj, fontObj] = prng.sample(colors, 2);

      const stimulusHtml = `<div style="text-align:center;padding:18px;font-size:2.4rem;font-weight:900;color:${fontObj.hex};border:1.5px solid rgba(255,255,255,0.15);border-radius:12px;background:rgba(0,0,0,0.2);">${wordObj.name_ar} (${wordObj.name_en})</div>`;

      const all = prng.shuffle([
        { ar: fontObj.name_ar, en: fontObj.name_en, isCorrect: true },
        ...colors.filter(c => c.hex !== fontObj.hex).map(c => ({ ar: c.name_ar, en: c.name_en, isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `اللون: ${it.ar}`,
        text_en: `Color: ${it.en}`,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار ستروب الكلاسيكي للانتباه وسرعة المعالجة (Stroop Effect):\nحدد **لون الخط الفعلي (Font Color)** الذي كُتبت به الكلمة بأقصى سرعة، مع مقاومة القراءة التلقائية لمعنى الكلمة:`,
        questionText_en: `In the clinical Stroop color-word interference task, report the **PHYSICAL FONT COLOR** of the displayed word, resisting semantic reading:`,
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: `الكلمة المكتوبة دلاليًا هي "${wordObj.name_ar}"، لكن اللون البصري للخط المستخدم في العرض هو اللون "${fontObj.name_ar}"، وهو المطلوب اختياره بدقة في (${options[correctIndex].id}).`,
        explanation_en: `The physical font color is ${fontObj.name_en}, requiring semantic suppression of the written word, confirming (${options[correctIndex].id}).`
      };
    }
  },

  // 3. Symbol Digit Coding Association (Wechsler Coding Paradigm)
  {
    id: 'speed_symbol_digit_coding',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      // Key: ★=1, ▲=2, ■=3, ●=4, ◆=5
      const keyMap = [
        { sym: '★', digit: 1 },
        { sym: '▲', digit: 2 },
        { sym: '■', digit: 3 },
        { sym: '●', digit: 4 },
        { sym: '◆', digit: 5 }
      ];

      // Query sequence of 4 symbols
      const querySyms = prng.sample(keyMap, 4);
      const correctDigits = querySyms.map(k => k.digit).join('');

      const keyTable = keyMap.map(k => `${k.sym} = ${k.digit}`).join('  |  ');
      const queryText = querySyms.map(k => k.sym).join('   ');

      const dist1 = querySyms.map(k => (k.digit % 5) + 1).join('');
      const dist2 = correctDigits.split('').reverse().join('');
      const dist3 = querySyms.map(k => (k.digit === 1 ? 5 : k.digit - 1)).join('');

      const all = prng.shuffle([correctDigits, dist1, dist2, dist3]);
      const correctIndex = all.indexOf(correctDigits);

      const options = all.map((str, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: str,
        text_en: str,
        value: str
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `باستخدام مفتاح التشفير السريع (Symbol-Digit Coding Key):\n[ ${keyTable} ]\n\nقم بفك تشفير سلسلة الرموز التالية بأسرع ما يمكن:\n[   ${queryText}   ]`,
        questionText_en: `Using the Symbol-Digit Coding Key:\n[ ${keyTable} ]\n\nRapidly decode the symbol string into its corresponding numeric sequence:\n[   ${queryText}   ]`,
        options,
        correctIndex,
        explanation_ar: `بمطابقة كل رمز برقمه المقابل في المفتاح المرجعي ينتج التسلسل الرقمي الدقيق: ${correctDigits} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Transcribing symbols according to the key produces ${correctDigits} (${options[correctIndex].id}).`
      };
    }
  },

  // 4. Feature Conjunction Visual Search
  {
    id: 'speed_feature_conjunction_search',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      // Find the "Red Triangle" in a field of Blue Triangles and Red Squares
      const drawField = (size = 180) => {
        let out = '';
        out += `<rect x="5" y="5" width="${size - 10}" height="${size - 10}" rx="8" fill="none" stroke="currentColor" stroke-opacity="0.2"/>`;
        // Distractors: 3 Red Squares, 3 Blue Triangles
        out += `<rect x="25" y="25" width="22" height="22" rx="3" fill="#ef4444"/>`;
        out += `<rect x="130" y="30" width="22" height="22" rx="3" fill="#ef4444"/>`;
        out += `<rect x="35" y="125" width="22" height="22" rx="3" fill="#ef4444"/>`;
        out += `<polygon points="90,20 102,42 78,42" fill="#3b82f6"/>`;
        out += `<polygon points="140,120 152,142 128,142" fill="#3b82f6"/>`;
        out += `<polygon points="30,80 42,102 18,102" fill="#3b82f6"/>`;
        // Target: Red Triangle (at center-right)
        out += `<polygon points="95,85 108,110 82,110" fill="#ef4444" stroke="#ffffff" stroke-width="1.5"/>`;
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-conjunction', 'Conjunction Search Field');
      };

      const options = [
        { id: 'A', text_ar: 'نعم، يوجد مثلث أحمر وحيد في منتصف اللوحة', text_en: 'Yes, exactly one red triangle is present near the center', isCorrect: true },
        { id: 'B', text_ar: 'لا يوجد أي مثلث أحمر في اللوحة إطلاقًا', text_en: 'No red triangle exists in the display', isCorrect: false },
        { id: 'C', text_ar: 'توجد ثلاثة مثلثات حمراء متجاورة', text_en: 'Three adjacent red triangles exist', isCorrect: false },
        { id: 'D', text_ar: 'كل الأشكال المعروضة مربعات زرقاء', text_en: 'All displayed shapes are blue squares', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مهمة البحث البصري المركب (Conjunction Search Task):\nافحص اللوحة بأسرع ما يمكن لتحديد وجود الهدف: **"مثلث أحمر"** بين مشتتات من المربعات الحمراء والمثلثات الزرقاء:`,
        questionText_en: `In the visual feature conjunction search paradigm:\nRapidly scan the field for the target: **"RED TRIANGLE"** amidst red squares and blue triangles:`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawField(180)}</div>`,
        options: shuffled,
        correctIndex,
        explanation_ar: `اللوحة تحتوي بالفعل على مثلث أحمر منفرد بالقرب من منتصف الشبكة، مما يثبت صحة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `The display contains exactly one red triangle target combining both target features, validating (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 5. Rapid Alphanumeric Transposition Spotting
  {
    id: 'speed_number_comparison_discrepancy',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      // Compare two 7-digit numbers: are they IDENTICAL or DIFFERENT?
      const isIdentical = prng.pick([true, false]);
      const baseNum = '8492751';
      const compNum = isIdentical ? '8492751' : '8492571'; // 75 transposed to 57

      const options = [
        { id: 'A', text_ar: isIdentical ? 'متطابقان تمامًا (Identical)' : 'مختلفان لوجود خانتين مقلوبتين (Different)', text_en: isIdentical ? 'Identical' : 'Different', isCorrect: true },
        { id: 'B', text_ar: isIdentical ? 'مختلفان لوجود خانتين مقلوبتين (Different)' : 'متطابقان تمامًا (Identical)', text_en: isIdentical ? 'Different' : 'Identical', isCorrect: false },
        { id: 'C', text_ar: 'العدد الثاني أكبر بمقدار 1000', text_en: 'Second number is greater by 1000', isCorrect: false },
        { id: 'D', text_ar: 'العدد الأول يحتوي خانات أكثر', text_en: 'First number has more digits', isCorrect: false }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `قارن بين الرقمين متعددي الخانات التاليين بأقصى سرعة إدراكية:\n\nالرقم الأول: [ ${baseNum} ]\nالرقم الثاني: [ ${compNum} ]\n\nهل الرقمان متطابقان كليًا أم مختلفان؟`,
        questionText_en: `Rapidly compare the two multi-digit numbers:\n\nNumber 1: [ ${baseNum} ]\nNumber 2: [ ${compNum} ]\n\nAre the two numbers completely identical or different?`,
        options,
        correctIndex,
        explanation_ar: isIdentical
          ? `الرقمان متطابقان تمامًا في جميع الخانات السبع (الخيار ${options[correctIndex].id}).`
          : `الرقمان مختلفان؛ حيث حدث قلب مكاني وتبديل بين الخانتين (75 في الأول أصبحت 57 في الثاني) (الخيار ${options[correctIndex].id}).`,
        explanation_en: isIdentical
          ? `All 7 digits match identically across both numbers (${options[correctIndex].id}).`
          : `Digits 5 and 7 are transposed in the second number (75 vs 57), confirming difference (${options[correctIndex].id}).`
      };
    }
  },

  // 6. Rapid Grid Symmetry Verification
  {
    id: 'speed_pattern_symmetry_rapid',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const isSymmetric = prng.pick([true, false]);

      const drawGrid = (symmetric, size = 150) => {
        let out = '';
        const s = 24;
        const offset = (size - s * 5) / 2;
        // 5x5 grid
        for (let r = 0; r < 5; r++) {
          for (let c = 0; c < 5; c++) {
            const x = offset + c * s;
            const y = offset + r * s;
            let filled = (r === 2 || c === 2);
            if (!symmetric && r === 1 && c === 4) filled = true; // asymmetric dot
            out += `<rect x="${x}" y="${y}" width="${s}" height="${s}" fill="${filled ? '#38bdf8' : 'rgba(255,255,255,0.05)'}" stroke="currentColor" stroke-opacity="0.3" stroke-width="1"/>`;
          }
        }
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-symgrid', 'Symmetry Verification Grid');
      };

      const options = [
        { id: 'A', text_ar: isSymmetric ? 'متماثل تمامًا حول المحور الرأسي (Symmetric)' : 'غير متماثل وبه عنصر شاذ يخل بالتناظر (Asymmetric)', text_en: isSymmetric ? 'Symmetric' : 'Asymmetric', isCorrect: true },
        { id: 'B', text_ar: isSymmetric ? 'غير متماثل وبه عنصر شاذ يخل بالتناظر (Asymmetric)' : 'متماثل تمامًا حول المحور الرأسي (Symmetric)', text_en: isSymmetric ? 'Asymmetric' : 'Symmetric', isCorrect: false },
        { id: 'C', text_ar: 'الشبكة خالية من أي تلوين', text_en: 'Empty grid', isCorrect: false },
        { id: 'D', text_ar: 'متماثل فقط إذا تم قلبه بزاوية 45 درجة', text_en: 'Symmetric only at 45 degrees', isCorrect: false }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احكم بأقصى سرعة إدراكية ممكنة على الشبكة المعروضة:\nهل النمط اللوني متماثل انعكاسيًا بدقة حول المحور الرأسي الأوسط؟`,
        questionText_en: `Rapidly evaluate the visual grid:\nIs the pixel pattern strictly symmetric across the vertical midline axis?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawGrid(isSymmetric, 160)}</div>`,
        options,
        correctIndex,
        explanation_ar: isSymmetric
          ? `النمط متطابق كليًا على جانبي المحور الرأسي الأوسط دون أي شذوذ (الخيار ${options[correctIndex].id}).`
          : `يوجد خلل في التناظر لوجود خلية ملونة في أحد الجانبين دون نظيرتها المقابلة (الخيار ${options[correctIndex].id}).`,
        explanation_en: isSymmetric
          ? `Bilateral vertical symmetry holds without deviation (${options[correctIndex].id}).`
          : `An asymmetric outlier pixel breaks bilateral reflection parity (${options[correctIndex].id}).`
      };
    }
  },

  // 7. Rapid Visual Oddity Elimination
  {
    id: 'speed_odd_one_out_rapid',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      // 4 shapes displayed in stimulus: 3 pointing Up, 1 pointing Down
      const oddIndex = prng.int(0, 3);

      const drawArrow = (isOdd) => (s) => DrawShapes.arrow(s * 0.75, isOdd ? 180 : 0, '#38bdf8');

      const drawStimulus = (size = 320) => {
        let out = '';
        const w = size / 4;
        for (let i = 0; i < 4; i++) {
          const cx = i * w + w / 2;
          const cy = 40;
          out += `<rect x="${i * w + 6}" y="6" width="${w - 12}" height="68" rx="8" fill="rgba(255,255,255,0.03)" stroke="currentColor" stroke-opacity="0.2"/>`;
          out += `<g transform="translate(${cx}, ${cy})">${drawArrow(i === oddIndex)(40)}</g>`;
          out += `<text x="${cx}" y="94" text-anchor="middle" font-size="12" font-weight="700" fill="currentColor">(${i + 1})</text>`;
        }
        return wrapSvg(out, `0 0 ${size} 105`, 'iq-svg-odd', 'Odd Shape Display');
      };

      const options = [0, 1, 2, 3].map(idx => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `الشكل رقم (${idx + 1})`,
        text_en: `Shape (${idx + 1})`,
        value: idx
      }));

      const correctIndex = oddIndex;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `حدد الشكل الشاذ المخالف لباقي الأشكال في الاتجاه بأسرع وقت ممكن:`,
        questionText_en: `Identify the directional outlier that differs from the other three symbols with maximum processing speed:`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawStimulus(320)}</div>`,
        options,
        correctIndex,
        explanation_ar: `ثلاثة من الأسهم تشير جميعها إلى الأعلى، بينما السهم الوحيد الشاذ يشير إلى الأسفل هو الشكل (${oddIndex + 1}) في الخيار (${options[correctIndex].id}).`,
        explanation_en: `Three arrows point directly upward, while the single outlier points downward at position (${oddIndex + 1}), matching option (${options[correctIndex].id}).`
      };
    }
  },

  // 8. Rapid Alphanumeric Cancellation Scan
  {
    id: 'speed_rapid_cancellation',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      // String of 15 letters with exactly N occurrences of letter 'س' (S)
      const targetCount = prng.pick([3, 4]);
      const sampleText = targetCount === 3
        ? 'ب - ت - س - ج - ح - خ - س - د - ر - ز - س - ص - ض - ط - ظ'
        : 'ب - س - ت - ج - س - ح - خ - س - د - ر - س - ز - ص - ض - ط';

      const distractors = [targetCount - 1, targetCount + 1, targetCount + 2];
      const allCounts = prng.shuffle([targetCount, ...distractors]);
      const correctIndex = allCounts.indexOf(targetCount);

      const options = allCounts.map((cnt, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${cnt} مرات`,
        text_en: `${cnt} times`,
        value: cnt
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مهمة الشطب البصري السريع (D2 Cancellation Test):\nامسح السلسلة التالية بأقصى سرعة بصرية وأحصِ عدد مرات تكرار الحرف المستهدف [ س ]:\n\n[ ${sampleText} ]`,
        questionText_en: `In the rapid visual cancellation paradigm, scan the sequence and count the exact occurrences of the target letter [ س / S ]:\n\n[ ${sampleText} ]`,
        options,
        correctIndex,
        explanation_ar: `الحرف المستهدف [ س ] تكرر بدقة ${targetCount} مرات في السلسلة المعروضة، وهو ما يطابق الخيار (${options[correctIndex].id}).`,
        explanation_en: `Scanning the string reveals precisely ${targetCount} occurrences of the target glyph (${options[correctIndex].id}).`
      };
    }
  },

  // 9. Rapid Duplicate Detection in Parallel Arrays
  {
    id: 'speed_perceptual_matching_pairs',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      // Array 1 vs Array 2: Exactly ONE item appears in both
      const arr1 = ['قلم', 'كتاب', 'ساعة', 'مفتاح'];
      const arr2 = ['هاتف', 'ساعة', 'حقيبة', 'نظارة'];
      const commonWord = 'ساعة (Watch)';

      const options = [
        { id: 'A', text_ar: commonWord, text_en: 'Watch', isCorrect: true },
        { id: 'B', text_ar: 'قلم (Pen)', text_en: 'Pen', isCorrect: false },
        { id: 'C', text_ar: 'هاتف (Phone)', text_en: 'Phone', isCorrect: false },
        { id: 'D', text_ar: 'لا توجد أي كلمة مشتركة', text_en: 'No common word', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `افحص المجموعتين التاليتين بأقصى سرعة واستخرج الكلمة الوحيدة المشتركة بينهما:\n• المجموعة الأولى: [ ${arr1.join(' ، ')} ]\n• المجموعة الثانية: [ ${arr2.join(' ، ')} ]`,
        questionText_en: `Rapidly identify the single common word present in both sets:\n• Set 1: [ Pen, Book, Watch, Key ]\n• Set 2: [ Phone, Watch, Bag, Glasses ]`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الكلمة الوحيدة المشتركة التي تظهر في كلتا المجموعتين هي "ساعة" (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `The intersection of both sets yields exclusively "Watch" (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 10. Rapid Dot-Probe Spatial Cueing
  {
    id: 'speed_dot_probe_orientation',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const isTop = prng.pick([true, false]);

      const drawProbe = (top, size = 140) => {
        let out = `<rect x="5" y="5" width="${size - 10}" height="${size - 10}" rx="8" fill="none" stroke="currentColor" stroke-opacity="0.2"/>`;
        out += `<line x1="${size / 2}" y1="20" x2="${size / 2}" y2="${size - 20}" stroke="currentColor" stroke-dasharray="2,2" opacity="0.3"/>`;
        const y = top ? size * 0.28 : size * 0.72;
        out += `<circle cx="${size / 2}" cy="${y}" r="8" fill="#38bdf8"/>`;
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-probe', 'Dot Probe');
      };

      const options = [
        { id: 'A', text_ar: isTop ? 'في النصف العلوي (Top Half)' : 'في النصف السفلي (Bottom Half)', text_en: isTop ? 'Top Half' : 'Bottom Half', isCorrect: true },
        { id: 'B', text_ar: isTop ? 'في النصف السفلي (Bottom Half)' : 'في النصف العلوي (Top Half)', text_en: isTop ? 'Bottom Half' : 'Top Half', isCorrect: false },
        { id: 'C', text_ar: 'في المركز الهندسي الدقيق', text_en: 'Exact center', isCorrect: false },
        { id: 'D', text_ar: 'على الحافة الخارجية تمامًا', text_en: 'Outer border', isCorrect: false }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مهمة الاستجابة السريعة للمسبار البصري (Dot-Probe Task):\nأين تموضع المسبار الدائري المضيء بأسرع نظرة بصرية خاطفة؟`,
        questionText_en: `In the Dot-Probe spatial cueing task, where is the luminous target probe situated upon rapid visual acquisition?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawProbe(isTop, 150)}</div>`,
        options,
        correctIndex,
        explanation_ar: `المسبار البصري يتموضع في ${isTop ? 'النصف العلوي' : 'النصف السفلي'} للوحة (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The visual probe is located strictly in the ${isTop ? 'Top' : 'Bottom'} quadrant (${options[correctIndex].id}).`
      };
    }
  },

  // 11. Rapid Mental Parity Check on Rotated Glyphs
  {
    id: 'speed_mental_rotation_speed',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      // Letter 'R' or 'F' rotated vs mirrored
      const isMirrored = prng.pick([true, false]);

      const drawGlyph = (mirrored, size = 110) => {
        const sx = mirrored ? -1 : 1;
        let out = `<g transform="translate(${size / 2}, ${size / 2}) rotate(45) scale(${sx}, 1)">`;
        out += `<text x="0" y="16" text-anchor="middle" font-size="44" font-weight="900" fill="#38bdf8">R</text>`;
        out += `</g>`;
        return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-glyph', 'Rotated Glyph');
      };

      const options = [
        { id: 'A', text_ar: isMirrored ? 'حرف معكوس مرآويًا (Mirrored Inverted)' : 'حرف عادي طبيعي لكنه مائل فقط (Normal Rotated)', text_en: isMirrored ? 'Mirrored Inverted' : 'Normal Rotated', isCorrect: true },
        { id: 'B', text_ar: isMirrored ? 'حرف عادي طبيعي لكنه مائل فقط (Normal Rotated)' : 'حرف معكوس مرآويًا (Mirrored Inverted)', text_en: isMirrored ? 'Normal Rotated' : 'Mirrored Inverted', isCorrect: false },
        { id: 'C', text_ar: 'حرف مقلوب رأسًا على عقب مرتين', text_en: 'Inverted twice', isCorrect: false },
        { id: 'D', text_ar: 'رمز هندسي لا يمثل حرفًا', text_en: 'Not a letter', isCorrect: false }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احكم بأقصى سرعة إدراكية على الحرف (R) المائل الموضح في الشكل:\nهل هو حرف طبيعي سليم تم تدويره فقط، أم أنه حرف معكوس مرآويًا (Mirrored)؟`,
        questionText_en: `Rapidly evaluate the rotated character (R):\nIs it a normal rotated letter or a laterally mirrored reflection?`,
        stimulusHtml: `<div style="display:flex;justify-content:center;padding:12px;">${drawGlyph(isMirrored, 120)}</div>`,
        options,
        correctIndex,
        explanation_ar: isMirrored
          ? `الحرف معكوس أفقيًا في مرآة، فلا يمكن بإعادة تدويره في المستوى أن يتطابق مع الحرف R القياسي (الخيار ${options[correctIndex].id}).`
          : `الحرف سليم بنيويًا وتم تدويره بزاويته فقط، فيمكن استعادته بالدوران الطبيعي (الخيار ${options[correctIndex].id}).`,
        explanation_en: isMirrored
          ? `The character exhibits chirality inversion (mirrored) which cannot be superposed by planar rotation (${options[correctIndex].id}).`
          : `The character preserves canonical chirality and is merely rotated (${options[correctIndex].id}).`
      };
    }
  },

  // 12. Rapid Visual Lexical Decision
  {
    id: 'speed_visual_lexical_decision',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const isReal = prng.pick([true, false]);
      const word = isReal ? 'حِكْمَة (Wisdom)' : 'خِصْفَط (Pseudo-word)';

      const options = [
        { id: 'A', text_ar: isReal ? 'كلمة عربية حقيقية صحيحة المعنى' : 'كلمة زائفة غير موجودة في المعاجم (Pseudo-word)', text_en: isReal ? 'Real Word' : 'Pseudo-word', isCorrect: true },
        { id: 'B', text_ar: isReal ? 'كلمة زائفة غير موجودة في المعاجم (Pseudo-word)' : 'كلمة عربية حقيقية صحيحة المعنى', text_en: isReal ? 'Pseudo-word' : 'Real Word', isCorrect: false },
        { id: 'C', text_ar: 'اسم علم لشخص تاريخي', text_en: 'Historical Name', isCorrect: false },
        { id: 'D', text_ar: 'حرف جر مركب', text_en: 'Compound Preposition', isCorrect: false }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مهمة القرار المعجمي البصري فائق السرعة (Lexical Decision Task):\nاحكم فورًا على اللفظ المعروض: [ ${word.split(' ')[0]} ]\nهل هو كلمة حقيقية ذات معنى في المعجم العربي أم كلمة زائفة؟`,
        questionText_en: `In the rapid Lexical Decision Task, evaluate the presented string immediately:\n[ ${word.split(' ')[0]} ]\nIs it a legitimate dictionary word or an artificial pseudo-word?`,
        options,
        correctIndex,
        explanation_ar: isReal
          ? `الكلمة ذات جذر ومعنى معجمي أصيل في اللغة العربية (الخيار ${options[correctIndex].id}).`
          : `اللفظ مركب عشوائيًا ولا أصل له في المعاجم العربية (الخيار ${options[correctIndex].id}).`,
        explanation_en: isReal
          ? `The string is a legitimate semantic entry in the lexicon (${options[correctIndex].id}).`
          : `The string is an artificial orthographic pseudo-word with no semantic entry (${options[correctIndex].id}).`
      };
    }
  },

  // 13. Rapid Consecutive Change Detection
  {
    id: 'speed_consecutive_change_detection',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'الحرف (ج) تحول إلى الحرف (خ)', text_en: 'Letter C changed to Kh', isCorrect: true },
        { id: 'B', text_ar: 'الحرف (أ) اختفى تمامًا', text_en: 'Letter A vanished', isCorrect: false },
        { id: 'C', text_ar: 'تم تبديل موضع الحرف (ب) مع (د)', text_en: 'Letter B swapped with D', isCorrect: false },
        { id: 'D', text_ar: 'المجموعتان متطابقتان تمامًا دون أي اختلاف', text_en: 'Both sets are identical', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `قارن بين اللوحتين بأسرع مسح بصري لاكتشاف التغير المفاجئ:\n• العرض 1: [ أ - ب - ج - د ]\n• العرض 2: [ أ - ب - خ - د ]\n\nما هو التغيير الدقيق الذي وقع بين العرضين؟`,
        questionText_en: `Rapidly compare the two brief visual displays to detect the single change:\n• Display 1: [ A - B - C - D ]\n• Display 2: [ A - B - Kh - D ]\n\nWhat exact alteration occurred?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `العنصر الثالث في الموقع 3 استُبدل من الحرف (ج) إلى الحرف (خ)، وهو التغيير الوحيد بين المجموعتين (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `Position 3 was substituted, confirming option (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 14. Dual Target Rapid RSVP Scan
  {
    id: 'speed_dual_target_rapid_scan',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'نعم، الرمز (★) موجود في السلسلة', text_en: 'Yes, symbol (★) is present', isCorrect: true },
        { id: 'B', text_ar: 'نعم، الرمز (▲) موجود في السلسلة', text_en: 'Yes, symbol (▲) is present', isCorrect: false },
        { id: 'C', text_ar: 'كلا الرمزين المستهدفين غير موجودين إطلاقًا', text_en: 'Neither target is present', isCorrect: false },
        { id: 'D', text_ar: 'كلا الرمزين ظهرا متجاورين معًا', text_en: 'Both targets appeared together', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `طُلب منك رصد أيٍّ من الهدفين: إما [ ★ ] أو [ ▲ ].\nافحص السلسلة السريعة التالية:\n[ ● - ■ - ◆ - ★ - ✚ - ⬟ ]\n\nأي الأهداف تم رصده في العرض؟`,
        questionText_en: `Your dual-target detection filter monitors for either [ ★ ] or [ ▲ ].\nScan the stream rapidly:\n[ ● - ■ - ◆ - ★ - ✚ - ⬟ ]\n\nWhich target was detected?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الرمز النجمي [ ★ ] يظهر بالفعل في الموقع الرابع، بينما المثلث [ ▲ ] غير موجود، مما يثبت صحة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Target (★) is detected at position 4 while the alternative target is absent, confirming (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 15. Rapid Temporal Duration Discrimination
  {
    id: 'speed_temporal_interval_discrimination',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'الوميض الثاني أطول زمنيًا بوضوح (0.8 ثانية مقابل 0.3 ثانية)', text_en: 'Flash 2 is noticeably longer (0.8s vs 0.3s)', isCorrect: true },
        { id: 'B', text_ar: 'الوميض الأول أطول زمنيًا', text_en: 'Flash 1 is longer', isCorrect: false },
        { id: 'C', text_ar: 'الوميضان متساويان في المدة تمامًا', text_en: 'Both flashes are identical in duration', isCorrect: false },
        { id: 'D', text_ar: 'الوميض الثاني لم يحدث أبدًا', text_en: 'Flash 2 never occurred', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار التمييز الزمني فائق الحساسية (Temporal Duration Discrimination):\n• الوميض الأول استمر لمدة: 300 ملي ثانية (0.3 ثانية).\n• الوميض الثاني استمر لمدة: 800 ملي ثانية (0.8 ثانية).\n\nما هو الاستنتاج الإدراكي الصارم للمقارنة بين الحدثين؟`,
        questionText_en: `In the psychophysical temporal duration discrimination task:\n• Flash 1 duration: 300 ms (0.3s).\n• Flash 2 duration: 800 ms (0.8s).\n\nWhat is the rigorous perceptual evaluation?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `800 ملي ثانية أطول من 300 ملي ثانية بأكثر من الضعف، مما يجعل الوميض الثاني أطول زمنيًا بوضوح إدراكي قاطع (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `800 ms strictly exceeds 300 ms, verifying (${shuffled[correctIndex].id}).`
      };
    }
  }
];
